import {
  alpha,
  Box,
  ButtonBase,
  CircularProgress,
  darken,
  decomposeColor,
  Grid,
  lighten,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from 'src/modules/member/models/memberSearchFormState';
import { memberTabs } from 'src/modules/member/models/memberTabs';
import numberWithSpaces from 'src/shared/functions/numberWithSpaces';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { MemberStatusTabKeysEnum } from 'src/shared/models/enums/memberStatusTabKeys.enum';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';

type ColoredBoxProps = {
  bgColor: string;

  isLoading: boolean;
  label: string;
  count: number;
  memberStatus: MemberStatusTabKeysEnum;
  /** Om angivet — föräldern styr fontSize på siffran (i px). Då hoppas intern auto-shrink. */
  numberFontSizePx?: number;
  /** Callback ref till siffrans DOM-element så föräldern kan mäta för synkad storlek över flera kort. */
  numberRef?: (el: HTMLElement | null) => void;
};

// Gul-förankrade ljushetsnivåer (Rec. 601 perceived Y') så alla färgade kort upplevs lika ljusa,
// oavsett hue:ns naturliga luminans (rött upplevs annars mörkare än grönt och gult vid samma lighten-värde).
const BG_BRIGHTNESS = 238;
const BORDER_BRIGHTNESS = 221;
const BG_HOVER_BRIGHTNESS = 230;
const BORDER_HOVER_BRIGHTNESS = 213;

const perceivedBrightness = (color: string): number => {
  const { values } = decomposeColor(color);
  return 0.299 * values[0] + 0.587 * values[1] + 0.114 * values[2];
};

const lightenToBrightness = (color: string, target: number): string => {
  const base = perceivedBrightness(color);
  if (base >= target) return color;
  const coef = Math.min(1, (target - base) / (255 - base));
  return lighten(color, coef);
};

export const ColoredBox = (props: ColoredBoxProps) => {
  const { bgColor, isLoading, label, count, memberStatus, numberFontSizePx, numberRef: externalNumberRef } = props;
  const managed = numberFontSizePx !== undefined || externalNumberRef !== undefined;
  const theme = useTheme();
  const navigate = useNavigate();
  const [loadingGrid, setLoadingGrid] = useState<boolean>(false);

  const isXS = useMediaQuery(theme.breakpoints.down('md'));
  const isMD = useMediaQuery(theme.breakpoints.down('xl'));

  const initialState = getDefaultMemberSearchFormState() as MemberSearchFormState;

  const [advancedSearchFilter, setAdvancedSearchFilter] = useLocalStorage<MemberSearchFormState>(LocalStorageKeys.MemberOverviewFilter, {
    ...initialState,
  });

  const handleClick = useCallback(() => {
    setLoadingGrid(true);
    setAdvancedSearchFilter({ ...advancedSearchFilter, ...{ searchString: '', status: [memberTabs.filter((x) => x.key === memberStatus)[0]] } });
    setTimeout(() => {
      navigate(RoutesEnum.Members);
    }, 1000);
  }, [advancedSearchFilter, memberStatus, navigate, setAdvancedSearchFilter]);

  const currentVariant = (isXS && 'caption') || (isMD && 'body2') || 'body2';

  // Shrink-to-fit för siffran: håller original-storleken (1.5rem på md+) när det får plats,
  // mäter med ResizeObserver och skalar bara ner när texten skulle överflöda. Min 13 px.
  // Om managed=true styr föräldern fontSize istället — vi exponerar bara ref:en uppåt.
  const cardRef = useRef<HTMLAnchorElement | null>(null);
  const numberRef = useRef<HTMLElement | null>(null);

  const setNumberRef = useCallback(
    (el: HTMLElement | null) => {
      numberRef.current = el;
      if (externalNumberRef) externalNumberRef(el);
    },
    [externalNumberRef],
  );

  useLayoutEffect(() => {
    if (managed) return undefined;
    const num = numberRef.current;
    const card = cardRef.current;
    if (!num || !card) return undefined;

    const baseFontSizePx = isXS ? 16 : 24;
    const minFontSizePx = 13;

    const fit = () => {
      num.style.fontSize = '';
      const available = num.clientWidth;
      const needed = num.scrollWidth;
      if (needed > available && available > 0) {
        const scaled = Math.max(minFontSizePx, baseFontSizePx * (available / needed));
        num.style.fontSize = `${scaled}px`;
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(card);
    return () => observer.disconnect();
  }, [count, isXS, managed]);

  return (
    <Grid item xs={4}>
      <ButtonBase sx={{ display: 'block', width: '100%', height: '100%', textAlign: 'left' }}>
        <Paper
          ref={cardRef}
          component='a'
          onClick={handleClick}
          sx={{
            display: 'block',
            height: '100%',
            boxSizing: 'border-box',
            backgroundColor: lightenToBrightness(bgColor, BG_BRIGHTNESS),
            color: darken(bgColor, 0.5),
            border: `1px solid ${lightenToBrightness(bgColor, BORDER_BRIGHTNESS)}`,
            borderRadius: 2,
            p: 1.5,
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: lightenToBrightness(bgColor, BG_HOVER_BRIGHTNESS),
              borderColor: lightenToBrightness(bgColor, BORDER_HOVER_BRIGHTNESS),
            },
            position: 'relative',
          }}
          elevation={0}
        >
          {loadingGrid && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(bgColor, 0.7),
                borderRadius: 2,
              }}
            >
              <CircularProgress sx={{ color: 'white' }} size={50} />
            </Box>
          )}
          <Typography sx={{ mb: 2 }} fontWeight={(thm) => thm.typography.fontWeightMedium} variant={currentVariant}>
            {label}
          </Typography>
          {(() => {
            const defaultFontSize = isXS ? '1rem' : '1.5rem';
            const fontSize = numberFontSizePx !== undefined ? `${numberFontSizePx}px` : defaultFontSize;
            if (isLoading) return <Skeleton sx={{ fontSize: defaultFontSize }} />;
            return (
              <Typography
                ref={setNumberRef}
                sx={{
                  fontSize,
                  fontWeight: (thm) => thm.typography.fontWeightBold,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'block',
                }}
              >
                {numberWithSpaces(count)}
              </Typography>
            );
          })()}
        </Paper>
      </ButtonBase>
    </Grid>
  );
};
